<template>
  <form class="bowler-contact-info-edit-form" @submit.prevent="submit">
    <FormTextInput
      v-model="formData.email"
      name="email"
      type="email"
      :label="$t('field-labels.email')"
      :error-label="formError?.email"
      is-disabled
      is-required
    />

    <FormTextInput
      v-model="formData.phone_number"
      name="phoneNumber"
      type="text"
      :label="$t('field-labels.phone')"
      :error-label="formError?.phone_number"
      is-required
    />

    <FormTextInput
      v-model="formData.street"
      name="streetAddress"
      type="text"
      :label="$t('field-labels.street-address')"
      is-required
    />

    <FormTextInput
      v-model="formData.postcode"
      name="postalCode"
      type="text"
      :label="$t('field-labels.postal-code')"
      :error-label="formError?.postcode"
      is-required
    />

    <FormTextInput
      v-model="formData.city"
      name="city"
      type="text"
      :label="$t('field-labels.city')"
      :error-label="formError?.city"
      is-required
    />

    <FormCheckBox
      v-model="formData.is_marketing_permitted"
      name="is_marketing_permitted"
      :label="$t('field-labels.is-marketing-permitted')"
      :error-label="formError?.is_marketing_permitted"
    />

    <div v-if="formError?.['detail']" class="error">
      {{ formError['detail'] }}
    </div>

    <FormButtonGroup>
      <FormButton :is-busy="isLoading">
        {{ $t('general.save') }}
      </FormButton>

      <FormButton
        :is-disabled="isLoading"
        is-secondary
        @click.prevent="$emit('dismiss')"
      >
        {{ $t('general.cancel') }}
      </FormButton>
    </FormButtonGroup>
  </form>
</template>

<script lang="ts" setup>
import axios from 'axios'
import isNil from 'lodash/isNil'
import { ref } from 'vue'

import FormButton from '@/components/common/FormButton.vue'
import FormButtonGroup from '@/components/common/FormButtonGroup.vue'
import FormCheckBox from '@/components/common/FormCheckBox.vue'
import FormTextInput from '@/components/common/FormTextInput.vue'
import { useBowlerStore } from '@/store/bowler-app/bowler'
import { BowlerEditableData } from '@/store/bowler-app/bowler.types'

interface BowlerEditableInfoFormData {
  email: string
  phone_number: string
  street: string
  postcode: string
  city: string
  country: string
  is_marketing_permitted: string
}

const emit = defineEmits(['dismiss', 'success'])

const props = defineProps({
  initialData: {
    type: Object,
    required: true,
  },
})

const bowler = useBowlerStore()

const formData = ref<BowlerEditableData>({
  email: props.initialData?.['email'] ?? '',
  phone_number: props.initialData?.['phoneNumber'] ?? '',
  street: props.initialData?.['streetAddress'] ?? '',
  postcode: props.initialData?.['postalCode'] ?? '',
  city: props.initialData?.['city'] ?? '',
  country: props.initialData?.['country'] ?? '',
  is_marketing_permitted: props.initialData?.['isMarketingPermitted'] ?? false,
})

const formError = ref<BowlerEditableInfoFormData | { detail: string } | null>()
const isLoading = ref<boolean>(false)

const submit = async (): Promise<void> => {
  isLoading.value = true
  formError.value = null

  try {
    if (!isNil(bowler.id)) {
      await bowler.update(String(bowler.id), formData.value)
      emit('success')
    }
    emit('dismiss')
  } catch (e) {
    console.log(e)

    formError.value = axios.isAxiosError(e)
      ? e.response?.data ?? {}
      : { detail: e as string }

    console.group('Failed updating bowler data')
    console.error(formError.value)
    console.groupEnd()
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

.bowler-contact-info-edit-form {
  display: flex;
  flex-flow: column nowrap;
  gap: 1em;

  .error {
    color: $color-danger;
  }
}
</style>
